import React, { useState, useEffect } from 'react';

const FriendliesUS = () => {
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzingRates, setAnalyzingRates] = useState(false);
  
  const [jobName, setJobName] = useState('');
  const [totalTransactions, setTotalTransactions] = useState(100);
  const [delayMode, setDelayMode] = useState('duration');
  const [durationDays, setDurationDays] = useState(7);
  const [secondsBetweenRequests, setSecondsBetweenRequests] = useState(600);
  const [visaPercentage, setVisaPercentage] = useState(50);
  const [mcPercentage, setMcPercentage] = useState(50);
  const [pricePoints, setPricePoints] = useState([{ price: 6.99, percentage: 100 }]);
  const [selectedGateways, setSelectedGateways] = useState({});
  const [gatewayRates, setGatewayRates] = useState({});
  const [currentUsaRates, setCurrentUsaRates] = useState({});
  const [internationalCounts, setInternationalCounts] = useState({});
  const [approvalRates, setApprovalRates] = useState({});
  const [approvedCounts, setApprovedCounts] = useState({});
  const [declinedCounts, setDeclinedCounts] = useState({});
  const [bulkUsaRateMin, setBulkUsaRateMin] = useState(58);
  const [bulkUsaRateMax, setBulkUsaRateMax] = useState(67);
  
  const [analysisStartDate, setAnalysisStartDate] = useState('');
  const [analysisEndDate, setAnalysisEndDate] = useState('');
  
  const [showGatewaySelector, setShowGatewaySelector] = useState(false);
  const [gatewaysForAnalysis, setGatewaysForAnalysis] = useState({});
  const [gatewaySearch, setGatewaySearch] = useState('');

  useEffect(() => {
    fetchGateways();
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);
    setAnalysisStartDate(last30Days.toISOString().split('T')[0]);
    setAnalysisEndDate(new Date().toISOString().split('T')[0]);
  }, []);

  const fetchGateways = async () => {
    try {
      const response = await fetch('/api/gateways', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setGateways(data);
      }
    } catch (err) {
      console.error('Error fetching gateways:', err);
    }
  };

  const analyzeCurrentRates = async () => {
    const selectedForAnalysis = Object.keys(gatewaysForAnalysis).filter(k => gatewaysForAnalysis[k]);
    
    if (selectedForAnalysis.length === 0) {
      alert('Please select at least one gateway for analysis using the "⚙️ Select" button');
      return;
    }

    setAnalyzingRates(true);
    try {
      const startDate = analysisStartDate.replace(/-/g, '');
      const endDate = analysisEndDate.replace(/-/g, '');
      const selectedIds = selectedForAnalysis.join(',');
      
      const response = await fetch(`/api/reports?startDate=${startDate}&endDate=${endDate}&gatewayIds=${selectedIds}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch rates');

      const data = await response.json();
      
      const rates = {};
      const intlCounts = {};
      const approvals = {};
      const approved = {};
      const declined = {};
      
      data.gateways.forEach(gw => {
        if (gatewaysForAnalysis[gw.gatewayId]) {
          rates[gw.gatewayId] = gw.usaRate;
          intlCounts[gw.gatewayId] = gw.internationalCount;
          approvals[gw.gatewayId] = gw.approvalRate || 0;
          approved[gw.gatewayId] = gw.approvedCount || 0;
          declined[gw.gatewayId] = gw.declinedCount || 0;
        }
      });

      setCurrentUsaRates(rates);
      setInternationalCounts(intlCounts);
      setApprovalRates(approvals);
      setApprovedCounts(approved);
      setDeclinedCounts(declined);
      
      alert(`USA rates analyzed successfully for ${Object.keys(rates).length} gateways!`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setAnalyzingRates(false);
    }
  };

  const selectAllGateways = () => {
    const all = {};
    gateways.forEach(gw => { all[gw.id] = true; });
    setSelectedGateways(all);
  };

  const unselectAllGateways = () => {
    setSelectedGateways({});
  };

  const addPricePoint = () => {
    setPricePoints([...pricePoints, { price: 9.99, percentage: 0 }]);
  };

  const removePricePoint = (index) => {
    setPricePoints(pricePoints.filter((_, i) => i !== index));
  };

  const updatePricePoint = (index, field, value) => {
    const updated = [...pricePoints];
    updated[index][field] = parseFloat(value) || 0;
    setPricePoints(updated);
  };

  const handleBulkRateApply = () => {
    const newRates = {};
    Object.keys(selectedGateways).forEach(gwId => {
      if (selectedGateways[gwId]) {
        const randomRate = bulkUsaRateMin + Math.random() * (bulkUsaRateMax - bulkUsaRateMin);
        newRates[gwId] = Math.round(randomRate * 10) / 10;
      }
    });
    setGatewayRates(newRates);
  };

  const getCurrentUsaCount = (gwId) => {
    const currentRate = currentUsaRates[gwId] || 0;
    const intlCount = internationalCounts[gwId] || 0;
    
    if (currentRate === 0) return 0;
    
    const totalCurrent = intlCount / (1 - currentRate / 100);
    return Math.round(totalCurrent - intlCount);
  };

  const calculateCardsToSend = (gwId) => {
    const targetUsaRate = gatewayRates[gwId] || 0;
    const currentRate = currentUsaRates[gwId] || 0;
    const currentUsaCount = getCurrentUsaCount(gwId);
    const intlCount = internationalCounts[gwId] || 0;
    
    if (targetUsaRate <= currentRate) return 0;
    if (intlCount === 0) return 0;
    
    if (targetUsaRate > 100) {
      const multiplier = targetUsaRate / 100;
      const cardsNeeded = (intlCount * multiplier) - currentUsaCount;
      return Math.max(0, Math.round(cardsNeeded));
    }
    
    if (currentUsaCount === 0) {
      if (targetUsaRate >= 100) return Math.round(intlCount * 10);
      const cardsNeeded = intlCount * (targetUsaRate / (100 - targetUsaRate));
      return Math.round(cardsNeeded);
    }
    
    const targetDecimal = targetUsaRate / 100;
    const cardsNeeded = (targetDecimal * intlCount - (1 - targetDecimal) * currentUsaCount) / (1 - targetDecimal);
    return Math.max(0, Math.round(cardsNeeded));
  };

  const calculateProjectedApproval = (gwId) => {
    const currentApproved = approvedCounts[gwId] || 0;
    const currentDeclined = declinedCounts[gwId] || 0;
    const cardsToSend = calculateCardsToSend(gwId);
    
    if (currentApproved === 0 && currentDeclined === 0) return 0;
    if (cardsToSend === 0) return approvalRates[gwId] || 0;
    
    const newApproved = currentApproved + cardsToSend;
    const newTotal = currentApproved + currentDeclined + cardsToSend;
    
    const projectedApproval = (newApproved / newTotal) * 100;
    
    return projectedApproval.toFixed(1);
  };

  const calculateProjectedRate = (gwId) => {
    const currentRate = currentUsaRates[gwId] || 0;
    const targetRate = gatewayRates[gwId] || 0;
    const cardsToSend = calculateCardsToSend(gwId);
    const currentUsaCount = getCurrentUsaCount(gwId);
    const intlCount = internationalCounts[gwId] || 0;
    
    if (targetRate <= currentRate) return currentRate.toFixed(1);
    if (cardsToSend === 0) return currentRate.toFixed(1);
    
    if (currentUsaCount === 0) {
      const projectedRate = (cardsToSend / (intlCount + cardsToSend)) * 100;
      return projectedRate.toFixed(1);
    }
    
    const totalCurrent = currentUsaCount + intlCount;
    const usaAfter = currentUsaCount + cardsToSend;
    const totalAfter = totalCurrent + cardsToSend;
    
    const projectedRate = (usaAfter / totalAfter) * 100;
    
    return projectedRate.toFixed(1);
  };

  const verifyCalculation = (gwId) => {
    const targetRate = gatewayRates[gwId] || 0;
    const projectedRate = parseFloat(calculateProjectedRate(gwId));
    
    const diff = Math.abs(projectedRate - targetRate);
    
    if (diff > 2.0) {
      return `⚠️ Calculation warning: projected ${projectedRate}% differs from target ${targetRate}% by ${diff.toFixed(1)} points (exceeds ±2.0 tolerance)`;
    }
    return null;
  };

  const getCardSendWarning = (gwId) => {
    const targetRate = gatewayRates[gwId] || 0;
    const currentRate = currentUsaRates[gwId] || 0;
    
    if (targetRate <= currentRate && targetRate > 0) {
      return `⚠️ Target rate (${targetRate}%) ≤ Current rate (${currentRate}%) - No cards will be sent`;
    }
    return null;
  };

  const calculateSimulation = () => {
    const selectedCount = Object.values(selectedGateways).filter(Boolean).length;
    if (selectedCount === 0) return null;

    const avgUsaRate = Object.entries(gatewayRates)
      .filter(([gwId]) => selectedGateways[gwId])
      .reduce((sum, [, rate]) => sum + rate, 0) / selectedCount;

    const totalPercentage = pricePoints.reduce((sum, pp) => sum + pp.percentage, 0);

    const totalCardsToSend = Object.keys(selectedGateways)
      .filter(gwId => selectedGateways[gwId])
      .reduce((sum, gwId) => sum + calculateCardsToSend(gwId), 0);

    return {
      selectedGatewaysCount: selectedCount,
      avgUsaRate: avgUsaRate.toFixed(1),
      totalTransactions: totalCardsToSend,
      visaCount: Math.round(totalCardsToSend * visaPercentage / 100),
      mcCount: Math.round(totalCardsToSend * mcPercentage / 100),
      pricePointsValid: Math.abs(totalPercentage - 100) < 0.1
    };
  };

  const calculateBatchCost = () => {
    if (!simulation) return null;

    const selectedGwIds = Object.keys(selectedGateways).filter(k => selectedGateways[k]);
    const totalCardsToSend = selectedGwIds.reduce((sum, gwId) => sum + calculateCardsToSend(gwId), 0);

    const visaCards = Math.round(totalCardsToSend * visaPercentage / 100);
    const mcCards = totalCardsToSend - visaCards;

    // Dynamic price point cost calculation
    const pricePointCosts = {};
    let fundingCost = 0;
    let lowPriceFees = 0;

    pricePoints.forEach(pp => {
      const cardsAtThisPrice = Math.round(totalCardsToSend * (pp.percentage / 100));
      const cost = cardsAtThisPrice * pp.price;
      const fee = pp.price < 5 ? cardsAtThisPrice * 0.10 : 0;
      pricePointCosts[pp.price] = { cards: cardsAtThisPrice, cost, percentage: pp.percentage, fee };
      fundingCost += cost;
      lowPriceFees += fee;
    });

    const cardGenerationCost = totalCardsToSend * 0.30;
    const totalCost = cardGenerationCost + fundingCost + lowPriceFees;

    return {
      totalCardsToSend,
      cardGenerationCost,
      pricePointCosts,
      fundingCost,
      totalCost,
      visaCards,
      mcCards
    };
  };

  const handleSubmit = async () => {
    const sim = calculateSimulation();
    if (!sim || !sim.pricePointsValid) {
      alert('Price points must total 100%');
      return;
    }

    if (!jobName.trim()) {
      alert('Please enter a job name');
      return;
    }

    const totalCards = sim.totalTransactions;
    const visaCards = Math.round(totalCards * visaPercentage / 100);
    const mcCards = totalCards - visaCards;

    const cardRequirements = [];
    
    pricePoints.forEach(pp => {
      const visaForPrice = Math.round(visaCards * pp.percentage / 100);
      const mcForPrice = Math.round(mcCards * pp.percentage / 100);
      
      if (visaForPrice > 0) {
        cardRequirements.push({
          cardType: 'visa',
          pricePoint: pp.price,
          required: visaForPrice
        });
      }
      
      if (mcForPrice > 0) {
        cardRequirements.push({
          cardType: 'mastercard',
          pricePoint: pp.price,
          required: mcForPrice
        });
      }
    });

    setLoading(true);

    try {
      const response = await fetch('/api/jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: jobName,
          totalTransactions: totalCards,
          delayMode: delayMode,
          durationDays: delayMode === 'duration' ? durationDays : null,
          secondsBetweenRequests: delayMode === 'manual' ? secondsBetweenRequests : null,
          startDate: new Date().toISOString().split('T')[0],
          endDate: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          visaPercentage,
          mcPercentage,
          pricePoints,
          selectedGateways: Object.keys(selectedGateways).filter(k => selectedGateways[k]),
          gatewayRates,
          gatewayQuotas: Object.keys(selectedGateways).filter(k => selectedGateways[k]).reduce((acc, gwId) => {
            acc[gwId] = calculateCardsToSend(gwId);
            return acc;
          }, {}),
          bulkUsaRateMin,
          bulkUsaRateMax,
          cardRequirements
        })
      });

      if (!response.ok) throw new Error('Failed to create job');

      alert('Draft Job created successfully! Check the Jobs menu to view card availability and create missing cardholders.');
      setJobName('');
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const simulation = calculateSimulation();
  
  const filteredGatewaysForModal = gateways.filter(gw => 
    gw.name.toLowerCase().includes(gatewaySearch.toLowerCase())
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Gateway Selector Modal */}
      {showGatewaySelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowGatewaySelector(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col m-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Select Gateways for Analysis</h3>
              <input
                type="text"
                placeholder="Search gateways..."
                value={gatewaySearch}
                onChange={(e) => setGatewaySearch(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const updated = { ...gatewaysForAnalysis };
                    filteredGatewaysForModal.forEach(gw => updated[gw.id] = true);
                    setGatewaysForAnalysis(updated);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
                >
                  ✓ Select All {gatewaySearch && `(${filteredGatewaysForModal.length})`}
                </button>
                <button
                  onClick={() => {
                    const updated = { ...gatewaysForAnalysis };
                    filteredGatewaysForModal.forEach(gw => updated[gw.id] = false);
                    setGatewaysForAnalysis(updated);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                >
                  ✗ Deselect All {gatewaySearch && `(${filteredGatewaysForModal.length})`}
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {filteredGatewaysForModal.map(gw => (
                  <label key={gw.id} className="flex items-center p-3 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={gatewaysForAnalysis[gw.id] || false}
                      onChange={(e) => setGatewaysForAnalysis({ ...gatewaysForAnalysis, [gw.id]: e.target.checked })}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="ml-3 text-sm text-gray-900">{gw.name}</span>
                  </label>
                ))}
              </div>
              {filteredGatewaysForModal.length === 0 && (
                <div className="text-center text-gray-500 py-8">No gateways found</div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowGatewaySelector(false);
                  setGatewaySearch('');
                }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowGatewaySelector(false);
                  setGatewaySearch('');
                }}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Apply Selection ({Object.values(gatewaysForAnalysis).filter(Boolean).length} selected)
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">🇺🇸 Friendlies US</h2>
        <p className="text-gray-600 mt-2">USA Traffic Distribution & Management</p>
      </div>

      {/* Job Name */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Job Name</label>
        <input
          type="text"
          value={jobName}
          onChange={(e) => setJobName(e.target.value)}
          placeholder="e.g., USA Traffic Distribution Q1 2026"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
        />
      </div>

      {/* Delay Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold mb-4">Delay Configuration</h3>
        <div className="flex gap-4 mb-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="delayMode"
              value="duration"
              checked={delayMode === 'duration'}
              onChange={(e) => setDelayMode(e.target.value)}
              className="w-4 h-4 text-primary-600"
            />
            <span className="ml-2 text-sm font-medium">Duration-Based (Auto-calculated)</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="radio"
              name="delayMode"
              value="manual"
              checked={delayMode === 'manual'}
              onChange={(e) => setDelayMode(e.target.value)}
              className="w-4 h-4 text-primary-600"
            />
            <span className="ml-2 text-sm font-medium">Manual Delay (Fixed seconds)</span>
          </label>
        </div>

        {delayMode === 'duration' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Duration (Days)</label>
            <input
              type="number"
              value={durationDays}
              onChange={(e) => setDurationDays(Math.max(1, parseInt(e.target.value) || 1))}
              min="1"
              placeholder="7"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {simulation ? `Auto-calculated: ~${Math.round((durationDays * 86400) / simulation.totalTransactions)}s per card (${Math.round(simulation.totalTransactions / durationDays)} cards/day)` : 'Configure job to see distribution'}
            </p>
          </div>
        )}

        {delayMode === 'manual' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Seconds Between Requests</label>
            <input
              type="number"
              value={secondsBetweenRequests}
              onChange={(e) => setSecondsBetweenRequests(Math.max(1, parseInt(e.target.value) || 600))}
              min="1"
              max="86400"
              placeholder="600"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              Manual delay: {secondsBetweenRequests}s per card (±15% randomization) • {Math.round(secondsBetweenRequests / 60)} minutes per card
              {simulation && ` • Estimated duration: ${Math.round((simulation.totalTransactions * secondsBetweenRequests) / 86400)} days`}
            </p>
          </div>
        )}
      </div>

      {/* Card Type Distribution */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold mb-4">Card Type Distribution</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-2">VISA %</label>
            <input
              type="number"
              value={visaPercentage}
              onChange={(e) => { 
                const val = parseFloat(e.target.value) || 0;
                setVisaPercentage(val);
                setMcPercentage(100 - val);
              }}
              min="0"
              max="100"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-2">Mastercard %</label>
            <input
              type="number"
              value={mcPercentage}
              onChange={(e) => {
                const val = parseFloat(e.target.value) || 0;
                setMcPercentage(val);
                setVisaPercentage(100 - val);
              }}
              min="0"
              max="100"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Price Points */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Price Points</h3>
          <button onClick={addPricePoint} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            + Add Price Point
          </button>
        </div>
        {pricePoints.map((pp, idx) => (
          <div key={idx} className="flex gap-4 mb-3">
            <input
              type="number"
              step="0.01"
              value={pp.price}
              onChange={(e) => updatePricePoint(idx, 'price', e.target.value)}
              placeholder="Price"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="number"
              value={pp.percentage}
              onChange={(e) => updatePricePoint(idx, 'percentage', e.target.value)}
              placeholder="Percentage"
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
            />
            {pricePoints.length > 1 && (
              <button onClick={() => removePricePoint(idx)} className="px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50">
                Remove
              </button>
            )}
          </div>
        ))}
        <p className="text-sm text-gray-600 mt-2">
          Total: {pricePoints.reduce((sum, pp) => sum + pp.percentage, 0).toFixed(1)}% (must equal 100%)
        </p>
      </div>

      {/* Current USA Rate Analysis */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold mb-4">Current USA Rate Analysis</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-700 mb-2">Analysis Start Date</label>
            <input
              type="date"
              value={analysisStartDate}
              onChange={(e) => setAnalysisStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-2">Analysis End Date</label>
            <input
              type="date"
              value={analysisEndDate}
              onChange={(e) => setAnalysisEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowGatewaySelector(true)}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
          >
            ⚙️ Select ({Object.values(gatewaysForAnalysis).filter(Boolean).length})
          </button>
          <button
            onClick={analyzeCurrentRates}
            disabled={analyzingRates}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {analyzingRates ? '⏳ Analyzing...' : '🔍 Analyze Current USA Rates'}
          </button>
        </div>
      </div>

      {/* Gateway Selection */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Select Gateways & USA Rates</h3>
          <div className="flex gap-2">
            <button
              onClick={selectAllGateways}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Select All
            </button>
            <button
              onClick={unselectAllGateways}
              className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Unselect All
            </button>
          </div>
        </div>
        {gateways.map((gw) => (
          <div key={gw.id} className="border-b border-gray-200 py-4 last:border-0">
            <div className="flex items-center gap-4 mb-2">
              <input
                type="checkbox"
                checked={selectedGateways[gw.id] || false}
                onChange={(e) => setSelectedGateways({ ...selectedGateways, [gw.id]: e.target.checked })}
                className="w-4 h-4"
              />
              <div className="flex-1">
                <span className="font-medium text-gray-900">{gw.name}</span>
                {currentUsaRates[gw.id] !== undefined && (
                  <div className="ml-3 flex gap-3 text-sm">
                    <span className="text-blue-600">
                      Current: {currentUsaRates[gw.id].toFixed(1)}% USA
                    </span>
                    {approvalRates[gw.id] !== undefined && (
                      <span className="text-green-600">
                        • Approval: {approvalRates[gw.id].toFixed(1)}%
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            {selectedGateways[gw.id] && (
              <div className="ml-8 mt-2 space-y-2">
                {currentUsaRates[gw.id] === undefined ? (
                  <p className="text-sm text-orange-600 font-medium">
                    ⚠️ Please analyze current USA rates first
                  </p>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm text-gray-700">
                        Target USA Rate:
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="400"
                          step="0.1"
                          value={gatewayRates[gw.id] || 0}
                          onChange={(e) => setGatewayRates({ ...gatewayRates, [gw.id]: parseFloat(e.target.value) || 0 })}
                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-right"
                          disabled={currentUsaRates[gw.id] === undefined}
                        />
                        <span className="text-sm">%</span>
                        <span className="text-sm text-green-600 font-medium ml-2">
                          Projected: {calculateProjectedRate(gw.id)}% USA
                        </span>
                        {approvalRates[gw.id] !== undefined && (
                          <span className="text-sm text-blue-600 font-medium ml-2">
                            • Approval: {calculateProjectedApproval(gw.id)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={Math.min(100, gatewayRates[gw.id] || 0)}
                      onChange={(e) => setGatewayRates({ ...gatewayRates, [gw.id]: parseFloat(e.target.value) })}
                      className="w-full"
                      disabled={currentUsaRates[gw.id] === undefined}
                    />
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current USA:</span>
                        <span className="font-medium">{getCurrentUsaCount(gw.id)} ({currentUsaRates[gw.id].toFixed(1)}%)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Cards to send:</span>
                        <span className="font-bold text-primary-600">{calculateCardsToSend(gw.id)}</span>
                      </div>
                    </div>
                    {getCardSendWarning(gw.id) && (
                      <p className="text-xs text-orange-600 font-medium">
                        {getCardSendWarning(gw.id)}
                      </p>
                    )}
                    {verifyCalculation(gw.id) && (
                      <p className="text-xs text-red-600 font-medium">
                        {verifyCalculation(gw.id)}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bulk USA Rate Range */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold mb-4">Bulk USA Rate Range</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-gray-700 mb-2">Min %</label>
            <input
              type="number"
              value={bulkUsaRateMin}
              onChange={(e) => setBulkUsaRateMin(parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-2">Max %</label>
            <input
              type="number"
              value={bulkUsaRateMax}
              onChange={(e) => setBulkUsaRateMax(parseFloat(e.target.value) || 0)}
              min="0"
              max="100"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
        <button
          onClick={handleBulkRateApply}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Apply Random USA Rates ({bulkUsaRateMin}% - {bulkUsaRateMax}%)
        </button>
      </div>

      {/* Simulation */}
      {simulation && (
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">📊 Simulation</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Selected Gateways</p>
              <p className="text-2xl font-bold text-gray-900">{simulation.selectedGatewaysCount}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg USA Rate</p>
              <p className="text-2xl font-bold text-blue-600">{simulation.avgUsaRate}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">VISA Cards</p>
              <p className="text-2xl font-bold text-purple-600">{simulation.visaCount}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">MC Cards</p>
              <p className="text-2xl font-bold text-orange-600">{simulation.mcCount}</p>
            </div>
          </div>
        </div>
      )}

      {/* Submit */}
      {/* Cost Preview */}
      {simulation && calculateBatchCost() && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">💰 Batch Cost Preview</h3>
          
          {/* Per-Gateway Breakdown */}
          <div className="mb-4 bg-white rounded-lg p-4 max-h-80 overflow-y-auto">
            <div className="text-sm font-medium text-gray-900 mb-3">Cost by Gateway:</div>
            <div className="space-y-2">
              {Object.keys(selectedGateways)
                .filter(gwId => selectedGateways[gwId])
                .map(gwId => {
                  const gw = gateways.find(g => g.id === gwId);
                  const cardsToSend = calculateCardsToSend(gwId);
                  const genCost = cardsToSend * 0.30;
                  
                  let fundingCost = 0;
                  let gwFee = 0;
                  pricePoints.forEach(pp => {
                    const cardsAtPrice = Math.round(cardsToSend * (pp.percentage / 100));
                    fundingCost += cardsAtPrice * pp.price;
                    if (pp.price < 5) gwFee += cardsAtPrice * 0.10;
                  });
                  
                  const subtotal = genCost + fundingCost + gwFee;
                  
                  return (
                    <div key={gwId} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-gray-900 truncate" title={gw?.name}>
                          {gw?.name || gwId}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {cardsToSend} cards: ${genCost.toFixed(2)} gen + ${fundingCost.toFixed(2)} funding
                          {(() => { let fee = 0; pricePoints.forEach(pp => { if (pp.price < 5) fee += Math.round(cardsToSend * (pp.percentage / 100)) * 0.10; }); return fee > 0 ? ` + $${fee.toFixed(2)} fee (<$5)` : ''; })()}
                        </div>
                      </div>
                      <div className="text-sm font-bold text-indigo-600 ml-4">
                        ${subtotal.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className={`grid ${Object.values(calculateBatchCost().pricePointCosts).some(d => d.fee > 0) ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-4`}>
            <div className="bg-white rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Virtual Card Activation (max)</div>
              <div className="text-2xl font-bold text-blue-600">
                ${calculateBatchCost().cardGenerationCost.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculateBatchCost().totalCardsToSend} cards × $0.30
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                ⓘ Only charged for new cardholders. Existing ones are reused for free.
              </div>
            </div>
            <div className="bg-white rounded p-4">
              <div className="text-sm text-gray-600 mb-1">Card Funding</div>
              <div className="text-2xl font-bold text-green-600">
                ${calculateBatchCost().fundingCost.toFixed(2)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {Object.entries(calculateBatchCost().pricePointCosts).map(([price, data], i, arr) => (
                  <span key={price}>
                    ${price} ({data.cards} cards): ${data.cost.toFixed(2)}
                    {i < arr.length - 1 && ' | '}
                  </span>
                ))}
              </div>
            </div>
            {Object.values(calculateBatchCost().pricePointCosts).some(d => d.fee > 0) && (
              <div className="bg-white rounded p-4">
                <div className="text-sm text-gray-600 mb-1">Low Price Fee (&lt;$5)</div>
                <div className="text-2xl font-bold text-orange-600">
                  ${Object.values(calculateBatchCost().pricePointCosts).reduce((s, d) => s + d.fee, 0).toFixed(2)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {Object.entries(calculateBatchCost().pricePointCosts).filter(([, d]) => d.fee > 0).map(([price, data], i, arr) => (
                    <span key={price}>${price}: {data.cards} × $0.10{i < arr.length - 1 && ' | '}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="bg-white rounded p-4 border-2 border-indigo-300">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">Total Batch Cost</div>
              <div className="text-3xl font-bold text-indigo-600">
                ${calculateBatchCost().totalCost.toFixed(2)}
              </div>
            </div>
            <div className="text-xs text-gray-600 mt-2">
              {calculateBatchCost().visaCards} VISA + {calculateBatchCost().mcCards} MC = {calculateBatchCost().totalCardsToSend} total cards
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading || !simulation || !simulation.pricePointsValid}
        className="w-full px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? '⏳ Creating Draft Job...' : '📋 Draft Job & Create CardHolders if Required'}
      </button>
    </div>
  );
};

export default FriendliesUS;
  